'use strict';

// import browser from "../support/browser.js";

let _MAX_CONCURRENT_TASKS = 4;

// if(browser.isAndroid()){
//     _MAX_CONCURRENT_TASKS = 10;
// }
const _STATUS_PENDING = 'pending';
const _STATUS_ACTIVE = 'active';

/**
 * Process a set of tasks and optionally limit the number of concurrent tasks.
 */
class Queue {
	constructor(maxConcurrentTasks) {
		this.maxConcurrentTasks = maxConcurrentTasks;
		this.tasks = [];
		this.taskPromises = [];
		// setInterval(() => {
		// 	console.debug(JSON.stringify(this.tasks));
		// }, 1000);
	}

	/**
	 * Add a task to the queue.
	 * @param {any} id A way for the client to identify the task.
	 * @param {function} task A function to be invoked. The function accepts the task ID and returns a Promise that resolves to the result of the task function when it is finished.
	 * @param {number} [retries=1] The number of times the task will be attempted if the task throws an error.
	 * @param {string} [name] A string to use for logging purposes.
	 * @param {boolean} [concurrentTask=false] Whether or not this task can be run concurrently with other tasks
	 * @returns {any} A Promise that resolves to the result of the task function.
	 */
	add(id, task, retries = 1, name, concurrentTask = false) {
		return new Promise((resolve, reject) => {
			const callback = (success, result) => {
				if (success) {
					resolve(result);
				} else {
					reject(result);
				}
			};

			// console.debug(() => {
			// 	return name
			// 		? `queue add - task '${name}' (${id}).`
			// 		: `queue add - task id '${id}'.`;
			// });

			this.tasks.push({
				callback,
				id,
				retries,
				status: _STATUS_PENDING,
				task,
				concurrentTask,
				name,
			});

			startTask.call(this);
		});
	}

	getNextTask(pendingTasks) {
		return pendingTasks[0];
	}

	removeSelectedPendingTasks(selector) {
		const removed = [];

		try {
			// find all pending tasks with the given type
			const toRemove = this.tasks.filter((item) => {
				return item.status === _STATUS_PENDING && selector(item);
			});
			// remove each selected pending task
			toRemove.forEach((item) => {
				removeTask.call(this, item.id);
				removed.push(item.id);
			});
		} catch (err) {
			console.error(`removeSelectedPendingTasks error - ${err}`);
		}
		return removed;
	}

	removeAllTasks() {
		this.tasks.length = 0;
		this.taskPromises.length = 0;
	}
}

/**
 * Create a queue instance.
 * @param {number} [maxConcurrentTasks=4] The maximum number of tasks that can run at the same time. A value of 0 or less means unlimited.
 * @returns {Queue} An instance of a Queue class.
 */
export default (maxConcurrentTasks = _MAX_CONCURRENT_TASKS) => {
	return new Queue(maxConcurrentTasks);
};

function handleTaskResult(success, task, result) {
	if (removeTask.call(this, task.id)) {
		let pending = this.tasks.filter((item) => {
			return item.status === _STATUS_PENDING;
		});

		if (success || task.retries < 1) {
			if (task.callback) {
				try {
					// So I had a situation where there were many tasks in requestQueue
					// but we weren't executing them. Hypothesis: this callback threw an
					// exception, so we never called setTimeout. As long as we also never
					// added any more tasks, we would never execute any more. Catch, log,
					// continue.
					task.callback(success, result);
				} catch (tcberr) {
					console.error(`tcberr! ${tcberr}`);
				}
			}
		} else {
			// If an error reset the status to pending unless there are no more retries.
			task.retries = task.retries - 1;
			task.status = _STATUS_PENDING;
			this.tasks.push(task);
		}
	} else {
		console.warn(
			`removeTask ${task.id} returned false; was the queue flushed?; not calling callback`
		);
	}

	// Let the stack unwind...
	setTimeout(() => {
		startTask.call(this);
	});
}

function removeTask(id) {
	let found = false;
	let index = this.tasks.findIndex((item) => {
		return item.id === id;
	});

	if (-1 < index) {
		found = true;
		this.tasks.splice(index, 1);
		// console.debug(
		// 	`queue removeTask - task '${id}' removed. ${this.tasks.length} tasks remain.`
		// );
	}

	index = this.taskPromises.findIndex((item) => {
		return item.id === id;
	});

	if (-1 < index) {
		this.taskPromises.splice(index, 1);
	}
	return found;
}

function startTask() {
	let pending = this.tasks.filter((item) => {
		return item.status === _STATUS_PENDING;
	});

	while (
		pending.length > 0 &&
		(this.maxConcurrentTasks < 1 ||
			this.taskPromises.length < this.maxConcurrentTasks)
	) {
		const item = this.getNextTask(pending);
		// console.info(`pending tasks ${JSON.stringify(pending)}`);

		// if the next task is not a concurrent task and there are other running tasks, do not start it
		if (!item.concurrentTask && this.taskPromises.length > 0) {
			// console.info(
			// 	`>>>>>>>> waiting to start non-concurrent task ${item.id} ${item.name}`
			// );
			break;
		}

		item.status = _STATUS_ACTIVE;
		this.taskPromises.push({
			id: item.id,
			promise: doTask.call(this, item),
		});
		// console.info(
		// 	`>>>>>>>> queue startTask - started task ${item.id} ${item.name} outstandingPromises: ${this.taskPromises.length}/${this.maxConcurrentTasks}`
		// );

		// re-assess the pending list since we've just started a task and we don't want to find it
		pending = this.tasks.filter((item) => {
			return item.status === _STATUS_PENDING;
		});
	}
}

function doTask(task) {
	// console.debug(`queue doTask - start task '${task.id}'`);
	return task
		.task(task.id)
		.then((result) => {
			const success =
				result && result.hasOwnProperty('success')
					? result.success
					: true;
			// console.debug(
			// 	`queue doTask - task '${task.id}' ${success ? 'was successful' : 'was NOT successful'}.`
			// );
			handleTaskResult.call(this, success, task, result);
		})
		.catch((err) => {
			console.warn(
				`queue doTask - task '${task.id}' was NOT successful.`
			);
			handleTaskResult.call(this, false, task, err);
		});
}
